import { FormikContext } from 'formik'
import { forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { createObjectInitialValues } from './helpers/create-object-initial-values'
import { CreateObjectValidationSchema } from './validators/create-object-validation-schema'
import { useFormik } from '../../../../common/hooks/formik'
import { PureInput } from '../../../../common/ui/pure-input/pure-input'
import { ObjectItem } from '../../../../entities/objects/types/object.types'
import { SelectLabel } from '../../../shared/components/select-label/select-label'

type ObjectModalProps = {
  onSubmit: (values: Omit<ObjectItem, 'id' | 'createdAt' | 'updatedAt' | 'type'>) => void
  initialValues: ObjectItem | null
}

export const ObjectModal = forwardRef(({ onSubmit, initialValues }: ObjectModalProps, ref) => {
  const { t } = useTranslation()

  const formik = useFormik({
    initialValues: createObjectInitialValues(initialValues),
    validationSchema: CreateObjectValidationSchema(),
    enableReinitialize: true,
    onSubmit: (values: any) => {
      onSubmit(values)
      formik.resetForm()
    },
  })

  useImperativeHandle(ref, () => ({
    submit: formik.submitForm,
  }))

  return (
    <FormikContext.Provider value={formik as any}>
      <form
        className="row"
        onSubmit={formik.handleSubmit}
      >
        <div className="col-12 col-md-3 px-1">
          <p className="mb-1">{t('objectsPage.objectName')}</p>
          <PureInput
            {...formik.getFieldProps('name')}
            error={formik.errors.name as string[]}
            placeholder={t('objectsPage.namePlaceholder')}
          />
        </div>

        <div className="col-12 col-md-3 px-1">
          <p className="mb-1">{t('objectsPage.address')}</p>
          <PureInput
            {...formik.getFieldProps('address')}
            error={formik.errors.address as string[]}
            placeholder={t('objectsPage.addressPlaceholder')}
          />
        </div>

        <div className="col-12 col-md-3 px-1">
          <p className="mb-1">{t('objectsPage.longitude')}</p>
          <PureInput
            {...formik.getFieldProps('longitude')}
            error={formik.errors.longitude as string[]}
            placeholder={t('objectsPage.longitudePlaceholder')}
          />
        </div>

        <div className="col-12 col-md-3 px-1">
          <p className="mb-1">{t('objectsPage.latitude')}</p>
          <PureInput
            {...formik.getFieldProps('latitude')}
            error={formik.errors.latitude as string[]}
            placeholder={t('objectsPage.latitudePlaceholder')}
          />
        </div>

        <div className="col-12 col-md-3 px-1 mt-3">
          <p className="mb-1">{t('objectsPage.objectType')}</p>
          <SelectLabel {...formik.getFieldProps('category', 'value')} />
        </div>
      </form>
    </FormikContext.Provider>
  )
})
